import { ModelIdSymbol } from '../constants.js';
import Order from '../order/index.js';
import assert from 'minimalistic-assert';
import { createIsInstance } from '../utils.js';
const FACTORY_SYMBOL = Symbol('OrderSet');
const createOrderSet = ({ items }) => new OrderSet({ items, initSymbol: FACTORY_SYMBOL });
function isObjectOrConvert(obj) {
    if (obj instanceof Order)
        return obj;
    return Order.fromJSON(obj);
}
function addToTxMap(txMap, txId, orderId) {
    if (txId) {
        const oids = txMap.get(txId) || [];
        if (oids.includes(orderId))
            return;
        txMap.set(txId, [...oids, orderId]);
    }
}
class OrderSet {
    _items;
    _txMap;
    _sortOrder;
    constructor({ items, initSymbol, }) {
        assert(initSymbol === FACTORY_SYMBOL, 'please use OrderSet.EMPTY or OrderSet.fromArray()');
        assert(items instanceof Map, 'items must be a Map');
        const itemArray = [...items.values()];
        itemArray.sort((a, b) => +b.date - +a.date);
        const txMap = new Map();
        for (const item of itemArray) {
            if (item.txIds) {
                item.txIds.forEach(({ txId }) => {
                    addToTxMap(txMap, txId, item.orderId);
                });
            }
            addToTxMap(txMap, item.fromTxId, item.orderId);
            addToTxMap(txMap, item.toTxId, item.orderId);
        }
        this._items = new Map(itemArray.map((item) => [String(item), item]));
        this._txMap = txMap;
        this._sortOrder = itemArray.map((tx) => tx.orderId);
    }
    static get [ModelIdSymbol]() {
        return 'OrderSet';
    }
    static _isInstance = createIsInstance(OrderSet);
    static [Symbol.hasInstance](x) {
        return this._isInstance(x);
    }
    static isInstance = OrderSet[Symbol.hasInstance];
    static EMPTY = new OrderSet({ items: new Map(), initSymbol: FACTORY_SYMBOL });
    static fromArray(arr) {
        if (arr == null)
            return OrderSet.EMPTY;
        return createOrderSet({
            items: new Map(arr.map(isObjectOrConvert).map((item) => [String(item), item])),
        });
    }
    add(item) {
        return this.addBulk([item]);
    }
    addBulk(items) {
        if (!items)
            return this;
        for (const item of items) {
            if (this.get(item)) {
                console.log(`OrderSet#add() already has #{item.orderId}`);
            }
        }
        return createOrderSet({
            items: new Map([...this._items.values(), ...items].map((item) => [String(item), item])),
        });
    }
    clone() {
        return createOrderSet({ items: new Map(this._items) });
    }
    delete(item) {
        const itemObj = this.get(item);
        if (!itemObj)
            return this;
        const items = new Map(this._items);
        items.delete(itemObj.orderId);
        return createOrderSet({ items });
    }
    equals(otherSet) {
        if (this._sortOrder.length !== otherSet._sortOrder.length)
            return false;
        const len = this._sortOrder.length;
        for (let i = 0; i < len; ++i) {
            if (this._sortOrder[i] !== otherSet._sortOrder[i])
                return false;
        }
        return true;
    }
    get(item) {
        return this._items.get(String(item));
    }
    getAt(index) {
        return this.get(this._sortOrder[index]);
    }
    getByTxId(txId, index = 0) {
        const oids = this._txMap.get(txId) || [];
        return this._items.get(oids[index]);
    }
    getAllByTxId(txId) {
        const oids = this._txMap.get(txId) || [];
        return oids.map((oid) => this._items.get(oid));
    }
    has(item) {
        return this._items.has(String(item));
    }
    toJSON() {
        return [...this].map((item) => item.toJSON());
    }
    toRedactedJSON() {
        return [...this].map((item) => item.toRedactedJSON());
    }
    filter(predicate) {
        return OrderSet.fromArray([...this].filter(predicate));
    }
    toJSONLegacy() {
        console.log('OrderSet: calling deprecated function orderSet.toJSONLegacy(), use orderSetToJSONLegacy() instead');
        return [...this].map((item) => item.toJSONLegacy());
    }
    union(otherSet) {
        const arr1 = [...this];
        const arr2 = [...otherSet];
        const both = [...arr1, ...arr2];
        return OrderSet.fromArray(both);
    }
    update(newItems) {
        const items = new Map(this._items);
        for (const newItem of newItems) {
            const thisItem = items.get(String(newItem));
            if (!thisItem) {
                console.log('OrderSet#update() order does not contain an `orderId`. Skipping...');
                continue;
            }
            items.set(String(newItem), thisItem.update(newItem));
        }
        return createOrderSet({ items });
    }
    [Symbol.iterator]() {
        return this._items.values();
    }
    get size() {
        return this._items.size;
    }
}
export default OrderSet;
